// useRehireConfirm — shared accept/reject/review handler that intercepts the
// backend's 409 ALREADY_ENGAGED warning (the candidate already has an active
// engagement at this org+unit) and turns it into a confirm dialog. On confirm,
// the same request is re-sent with confirm_rehire=true. Used by both the
// standalone ApplicationsPage and the inline NeedApplicationsSection so the
// warn-and-confirm behaviour is identical in both places.
import { useCallback, useState } from 'react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';

type Status = 'reviewing' | 'accepted' | 'rejected';

interface Pending {
    applicationId: string;
    status: Status;
    decisionNote?: string;
}

export function useRehireConfirm(refresh: () => Promise<void>) {
    const [pending, setPending] = useState<Pending | null>(null);
    const [busy, setBusy] = useState(false);

    const send = useCallback(
        async (applicationId: string, status: Status, decisionNote: string | undefined, confirmRehire: boolean) => {
            await jobApplicationApi.updateApplicationStatus(applicationId, {
                status,
                decision_note: decisionNote,
                confirm_rehire: confirmRehire || undefined,
            });
            await refresh();
        },
        [refresh],
    );

    const handleStatusUpdate = useCallback(
        async (applicationId: string, status: Status, decisionNote?: string) => {
            try {
                await send(applicationId, status, decisionNote, false);
            } catch (err) {
                // The kit's ApiClient interceptor already throws a parsed ApiError
                // ({ code, status, ... }), so read its fields directly — do NOT
                // re-run parseApiError (it expects a raw axios error and would
                // re-wrap this as UNKNOWN_ERROR / status 0, losing the 409).
                const e = err as { code?: string; status?: number };
                if (e?.code === 'ALREADY_ENGAGED' || e?.status === 409) {
                    setPending({ applicationId, status, decisionNote });
                    return;
                }
                console.error('Failed to update application status:', err);
            }
        },
        [send],
    );

    const confirmRehire = useCallback(async () => {
        if (!pending) return;
        setBusy(true);
        try {
            await send(pending.applicationId, pending.status, pending.decisionNote, true);
            setPending(null);
        } catch (err) {
            console.error('Re-hire confirmation failed:', err);
        } finally {
            setBusy(false);
        }
    }, [pending, send]);

    return {
        handleStatusUpdate,
        rehireDialog: {
            open: !!pending,
            onOpenChange: (open: boolean) => { if (!open) setPending(null); },
            title: 'Personen är redan anställd',
            description:
                'Den här personen har redan en aktiv anställning hos er enhet. Vill du acceptera ansökan ändå?',
            confirmLabel: 'Anställ ändå',
            cancelLabel: 'Avbryt',
            onConfirm: confirmRehire,
            isBusy: busy,
        },
    };
}
